import { CREATED, NO_CONTENT, NOT_FOUND, OK } from 'http-status-codes';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import { safeResolveAsync } from '../../utils';
import { getPropFromNormalized } from '../../utils/redux';
import { extractParamsFromUrl } from '../../utils/saga';

import * as api from './api';
import * as duck from './duck';
import { actions, types } from './duck';
import { actions as affiliateActions, types as affiliateTypes } from '../affiliateBusiness/duck';

export function* getBusinessCoupons() {
  try {
    yield put(actions.getBusinessCouponsRequest());

    const response = yield api.getBusinessCoupons();

    if (response.status === OK) {
      yield put(actions.getBusinessCouponsSuccess(response.data));
    } else {
      yield put(actions.getBusinessCouponsFailure(response.data.detail));
    }
  } catch (error) {
    yield put(actions.getBusinessCouponsFailure(error.message));
  }
}

export function* getBusinessCouponsNextPage() {
  try {
    const nextPageUrl = yield select(state =>
      getPropFromNormalized(state.coupons, duck.couponsEntity.key, 'next'),
    );

    if (!nextPageUrl) {
      return;
    }

    const params = extractParamsFromUrl(nextPageUrl);

    yield put(actions.getBusinessCouponsNextPageRequest());

    const response = yield api.getBusinessCoupons(params);

    if (response.status === OK) {
      yield put(actions.getBusinessCouponsNextPageSuccess(response.data));
    } else if (response.status === NOT_FOUND) {
      yield put(actions.getBusinessCouponsNextPageNotFound());
    } else {
      yield put(actions.getBusinessCouponsNextPageFailure(response.data));
    }
  } catch (error) {
    yield put(actions.getBusinessCouponsNextPageFailure(error.message));
  }
}

export function* getWebsiteCityCoupons({ params }) {
  try {
    yield put(actions.getWebsiteCityCouponsRequest());

    const response = yield api.getWebsiteCityCoupons(params);

    if (response.status === OK) {
      yield put(actions.getWebsiteCityCouponsSuccess(response.data));
    } else {
      yield put(actions.getWebsiteCityCouponsFailure(response.data.detail));
    }
  } catch (error) {
    yield put(actions.getWebsiteCityCouponsFailure(error.message));
  }
}

export function* getWebsiteCityCouponsNextPage({ params }) {
  try {
    const nextPageUrl = yield select(
      state => state.coupons.result.websiteCityCoupons.next
    );

    if (!nextPageUrl) {
      return;
    }

    const nextPageParams = extractParamsFromUrl(nextPageUrl);

    yield put(actions.getWebsiteCityCouponsNextPageRequest());

    const response = yield api.getWebsiteCityCoupons({
      ...params,
      ...nextPageParams,
    });

    if (response.status === OK) {
      yield put(actions.getWebsiteCityCouponsNextPageSuccess(response.data));
    } else if (response.status === NOT_FOUND) {
      yield put(actions.getWebsiteCityCouponsNextPageNotFound());
    } else {
      yield put(actions.getWebsiteCityCouponsNextPageFailure(response.data.detail));
    }
  } catch (error) {
    yield put(actions.getWebsiteCityCouponsNextPageFailure(error.message));
  }
}

export function* publishCoupon({ id, resolve }) {
  try {
    yield put(actions.publishCouponRequest(id));
    const response = yield api.publishCoupon(id);
    if (response.status === OK) {
      yield put(actions.publishCouponSuccess(response.data));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        throw Error(response.data[0]);
      } else {
        throw Error(response.data.detail);
      }
    }
  } catch (error) {
    yield put(actions.publishCouponFailure(error.message));
    yield safeResolveAsync(resolve, { success: false });
  }
}

export function* deleteCoupon({ id, resolve }) {
  try {
    yield put(actions.deleteCouponRequest(id));
    const response = yield api.deleteCoupon(id);
    if (response.status === NO_CONTENT) {
      yield put(actions.deleteCouponSuccess(id));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        throw Error(response.data[0]);
      } else {
        throw Error(response.data.detail);
      }
    }
  } catch (error) {
    yield put(actions.deleteCouponFailure(error.message));
    yield safeResolveAsync(resolve, { success: false });
  }
}

export function* sponsorCoupon({ id, resolve }) {
  try {
    yield put(actions.sponsorCouponRequest(id));
    const response = yield api.sponsorCoupon(id);
    if (response.status === OK) {
      yield put(actions.sponsorCouponSuccess(response.data));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        throw Error(response.data[0]);
      } else {
        throw Error(response.data.detail);
      }
    }
  } catch (error) {
    yield put(actions.sponsorCouponFailure(error.message));
    yield safeResolveAsync(resolve, { success: false });
  }
}

export function* republishCoupon({ data, resolve }) {
  try {
    yield put(actions.republishCouponRequest(data.id));
    const response = yield api.republishCoupon(data);
    if (response.status === CREATED) {
      yield put(actions.republishCouponSuccess(response.data));
      yield safeResolveAsync(resolve, { success: true });
    } else {
      if (Array.isArray(response.data)) {
        throw Error(response.data[0]);
      } else {
        throw Error(response.data.detail);
      }
    }
  } catch (error) {
    yield put(actions.republishCouponFailure(error.message));
    yield safeResolveAsync(resolve, { success: false });
  }
}

export function* getViewCounts({ couponIds }) {
  try {
    yield put(actions.getViewCountsRequest());
    const response = yield api.getViewCounts(couponIds);
    if (response.status === OK) {
      yield put(actions.getViewCountsSuccess(response.data));
    } else {
      console.error("Saga: API failed with:", response.data.detail);
      yield put(actions.getViewCountsFailure(response.data.detail));
    }
  } catch (error) {
    console.error("Saga: Error in getViewCounts:", error.message);
    yield put(actions.getViewCountsFailure(error.message));
  }
}

// Worker: fetch coupons for a single business
function* fetchCouponsForBusiness(business) {
  // console.log('[Saga] fetchCouponsForBusiness - businessId:', business.id, 'name:', business.name);
  yield put(actions.fetchAllAffiliateCouponsBusinessRequest(business.id));
  try {
    // console.log('[Saga] fetchCouponsForBusiness - calling api.getAffiliateCoupons:', business.id);
    const response = yield call(api.getAffiliateCoupons, business.id);
    // console.log('[Saga] fetchCouponsForBusiness - response status:', response.status, 'businessId:', business.id);
    if (response.status === OK) {
      // console.log('[Saga] fetchCouponsForBusiness - SUCCESS businessId:', business.id, 'data:', response.data);
      yield put(actions.fetchAllAffiliateCouponsBusinessSuccess(business.id, response.data));
    } else {
      // console.warn('[Saga] fetchCouponsForBusiness - FAILED businessId:', business.id, 'response:', response.data);
      yield put(actions.fetchAllAffiliateCouponsBusinessFailure(
        business.id,
        (response.data && response.data.detail) || 'Failed to fetch'
      ));
    }
  } catch (error) {
    // console.error('[Saga] fetchCouponsForBusiness - THREW businessId:', business.id, 'error:', error.message);
    yield put(actions.fetchAllAffiliateCouponsBusinessFailure(business.id, error.message));
  }
}

// Root watcher: fetch all businesses, then fan out in parallel
export function* fetchAllAffiliateCoupons() {
  console.log('[Saga] fetchAllAffiliateCoupons - ENTERED');
  try {
    yield put(actions.fetchAllAffiliateCouponsRequest());
    console.log('[Saga] fetchAllAffiliateCoupons - dispatched request action');

    let businesses = yield select(state =>
      state.affiliateBusiness &&
      state.affiliateBusiness.data &&
      state.affiliateBusiness.data.results
    );
    console.log('[Saga] fetchAllAffiliateCoupons - businesses from state:', businesses);

    if (!businesses || businesses.length === 0) {
      console.log('[Saga] fetchAllAffiliateCoupons - no businesses in state, fetching...');
      yield put(affiliateActions.getAffiliateBusinesses());
      console.log('[Saga] fetchAllAffiliateCoupons - waiting for GET_AFFILIATE_BUSINESSES_SUCCESS, type:', affiliateTypes.GET_AFFILIATE_BUSINESSES_SUCCESS);
      yield take(affiliateTypes.GET_AFFILIATE_BUSINESSES_SUCCESS);
      console.log('[Saga] fetchAllAffiliateCoupons - GET_AFFILIATE_BUSINESSES_SUCCESS received');
      businesses = yield select(state =>
        state.affiliateBusiness &&
        state.affiliateBusiness.data &&
        state.affiliateBusiness.data.results
      );
      console.log('[Saga] fetchAllAffiliateCoupons - businesses after fetch:', businesses);
    }

    if (!businesses || businesses.length === 0) {
      console.warn('[Saga] fetchAllAffiliateCoupons - still no businesses, completing early');
      yield put(actions.fetchAllAffiliateCouponsComplete());
      return;
    }

    console.log('[Saga] fetchAllAffiliateCoupons - fanning out for', businesses.length, 'businesses');
    yield all(businesses.map(business => call(fetchCouponsForBusiness, business)));

    console.log('[Saga] fetchAllAffiliateCoupons - all businesses done, dispatching complete');
    yield put(actions.fetchAllAffiliateCouponsComplete());
  } catch (error) {
    console.error('[Saga] fetchAllAffiliateCoupons - CAUGHT ERROR:', error.message, error);
    yield put(actions.fetchAllAffiliateCouponsComplete());
  }
}

// Newest published coupons across the whole system (admin analytics sidebar).
// Reuses coupons/filtered/, which is already ordered -created_at.
export function* getNewestCoupons({ params }) {
  try {
    yield put(actions.getNewestCouponsRequest());
    const response = yield call(api.getFilteredCoupons, {
      published_only: true,
      ...params,
    });
    if (response.status === OK) {
      yield put(actions.getNewestCouponsSuccess(response.data));
    } else {
      yield put(
        actions.getNewestCouponsFailure(
          (response.data && response.data.detail) || 'Failed to fetch newest coupons',
        ),
      );
    }
  } catch (error) {
    yield put(actions.getNewestCouponsFailure(error.message));
  }
}

export default function* saga() {
  yield takeLatest(types.GET_BUSINESS_COUPONS, getBusinessCoupons);
  yield takeLatest(types.GET_BUSINESS_COUPONS_NEXT_PAGE, getBusinessCouponsNextPage);
  yield takeLatest(types.GET_WEBSITE_CITY_COUPONS, getWebsiteCityCoupons);
  yield takeLatest(types.GET_WEBSITE_CITY_COUPONS_NEXT_PAGE, getWebsiteCityCouponsNextPage);
  yield takeLatest(types.PUBLISH_COUPON, publishCoupon);
  yield takeLatest(types.DELETE_COUPON, deleteCoupon);
  yield takeLatest(types.SPONSOR_COUPON, sponsorCoupon);
  yield takeLatest(types.REPUBLISH_COUPON, republishCoupon);
  yield takeLatest(types.GET_VIEW_COUNTS, getViewCounts);
  yield takeLatest(types.FETCH_ALL_AFFILIATE_COUPONS, fetchAllAffiliateCoupons);
  yield takeLatest(types.GET_NEWEST_COUPONS, getNewestCoupons);
}